import {
	DataGrid,
	GridColDef,
	GridRenderCellParams,
	GridSelectionModel,
} from "@mui/x-data-grid";
import {
	Box,
	TextField,
	Stack,
	Typography,
	Select,
	MenuItem,
	Button,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Paper,
	IconButton,
} from "@mui/material";
import {
	FeatureDeviceModel,
	GetNsureFeatureCategoryDisplayName,
	GetNsureFeatureTypeDisplayName,
} from "Infrastructure/Utils/Nsure/FeatureUtils";
import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import {
	FeatureEnableDisableMessage,
	FeatureEnableDisableModel,
} from "Infrastructure/Models/Messages/Messages";
import { csCZ } from "@mui/x-data-grid/locales";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { v4 as uuidv4 } from "uuid";
import { DeviceForm } from "Infrastructure/Utils/Nsure/DeviceUtils";
import { BlConfirmButton } from "Shared/Buttons/BlConfirmButton";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import useDebounce from "Infrastructure/Hooks/useDebounce";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { HubConnectionState } from "@microsoft/signalr";
import React from "react";
import { BlTooltip } from "Shared/BlTooltip";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SaveIcon from "@mui/icons-material/Save";

type Props = {
	deviceForm: DeviceForm;
	featureDeviceList: FeatureDeviceModel[];
	hubConnection: any;
	closeDialog: any;
	onEnableDisable: (message: FeatureEnableDisableMessage) => void;
	featuresToConfirm: FeatureEnableDisableModel[];
	setFeaturesToConfirm: (data: FeatureEnableDisableModel[]) => void;
	isHistoryLoaded?: boolean;
};

const FeatureTable: FC<Props> = ({
	deviceForm,
	featureDeviceList,
	hubConnection,
	closeDialog,
	onEnableDisable,
	featuresToConfirm,
	setFeaturesToConfirm,
	isHistoryLoaded = false,
}) => {
	const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
	const [selectedFeatureType, setSelectedFeatureType] = useState<
		string | number
	>("");
	const [selectedFeatureCategory, setSelectedFeatureCategory] = useState<
		string | number
	>("");
	const [filterText, setFilterText] = useState("");

	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const debouncedFilterText = useDebounce(filterText, 500);

	const columns = useMemo<GridColDef[]>(
		() => [
			{
				field: "isEnabled",
				type: "boolean",
				width: 120,
				headerAlign: "left",
				align: "left",
				sortable: false,
				renderHeader: () => <strong>{"IsEnabled"}</strong>,
				renderCell: (params: GridRenderCellParams) => {
					const featureToConfirm = featuresToConfirm.find(
						(f) => f.featureCode === params.row.code
					);
					if (!!featureToConfirm && featureToConfirm.doEnable !== undefined) {
						const beforeIcon =
							deviceForm.devices.length === 1 ? (
								params.value ? (
									<CheckCircleIcon color="success" fontSize="small" />
								) : (
									<CancelIcon color="error" fontSize="small" />
								)
							) : (
								<QuestionMarkIcon color="disabled" fontSize="small" />
							);
						const afterIcon = featureToConfirm?.doEnable ? (
							<CheckCircleIcon color="success" fontSize="small" />
						) : (
							<CancelIcon color="error" fontSize="small" />
						);
						return (
							<Stack direction="row" alignItems="center" spacing={1}>
								{beforeIcon}
								<span>→</span>
								{afterIcon}
							</Stack>
						);
					}

					return deviceForm.devices.length === 1 ? (
						params.value ? (
							<CheckCircleIcon color="success" fontSize="small" />
						) : (
							<CancelIcon color="error" fontSize="small" />
						)
					) : (
						<QuestionMarkIcon color="disabled" fontSize="small" />
					);
				},
			},
			{
				field: "code",
				flex: 1,
				sortable: true,
				renderHeader: () => <strong>{"FeatureCode"}</strong>,
				renderCell: (params: GridRenderCellParams) => (
					<Typography>{params.value}</Typography>
				),
			},
			{
				field: "displayName",
				flex: 1,
				sortable: true,
				renderHeader: () => <strong>{"Název"}</strong>,
			},
			{
				field: "featureType",
				sortable: true,
				renderHeader: () => <strong>{"Typ"}</strong>,
				renderCell: (params: GridRenderCellParams) => (
					<Typography>
						{GetNsureFeatureTypeDisplayName(params.value)}
					</Typography>
				),
			},
			{
				field: "featureCategory",
				sortable: true,
				width: 170,
				renderHeader: () => <strong>{"Kategorie"}</strong>,
				renderCell: (params: GridRenderCellParams) => (
					<Typography>
						{GetNsureFeatureCategoryDisplayName(params.value)}
					</Typography>
				),
			},
			{
				field: "isForAccounts",
				type: "boolean",
				sortable: true,
				renderHeader: () => <strong>{"Pro účty"}</strong>,
			},
			{
				field: "isPaid",
				type: "boolean",
				sortable: true,
				renderHeader: () => <strong>{"Hrazeno"}</strong>,
			},
			{
				field: "actions",
				headerName: "",
				sortable: false,
				renderCell: (params: GridRenderCellParams) => {
					const inConfirmArray = featuresToConfirm.some(
						(f) => f.featureCode === params.row.code
					);
					return inConfirmArray ? (
						<Button
							onClick={(event) => {
								event.stopPropagation();
								handleRemoveFeature(params.row.code);
							}}
							color="error"
							size="small"
						>
							Zrušit
						</Button>
					) : null;
				},
			},
		],
		[featuresToConfirm, deviceForm.devices.length]
	);

	const rows = useMemo(() => {
		return featureDeviceList.map((featureDevice, index) => ({
			id: index,
			code: featureDevice.feature.code,
			displayName: featureDevice.feature.displayName,
			isEnabled: featureDevice.feature.isEnabled,
			featureType: featureDevice.feature.featureType,
			featureCategory: featureDevice.feature.featureCategory,
			isPaid: featureDevice.feature.isPaid,
		}));
	}, [featureDeviceList]);

	const filteredRows = useMemo(() => {
		return rows.filter((row) => {
			const matchesFilterText =
				row.code?.toLowerCase().includes(debouncedFilterText.toLowerCase()) ??
				false;

			const matchesFeatureType =
				selectedFeatureType === "" || row.featureType === selectedFeatureType;

			const matchesFeatureCategory =
				selectedFeatureCategory === "" ||
				row.featureCategory === selectedFeatureCategory;

			return matchesFilterText && matchesFeatureType && matchesFeatureCategory;
		});
	}, [rows, debouncedFilterText, selectedFeatureType, selectedFeatureCategory]);

	const handleEnableDisable = (doEnable: boolean) => {
		const selectedFeatures = selectionModel.map((id) =>
			rows.find((row) => row.id === id)
		);
		const newFeatures = selectedFeatures.map((feature) => ({
			featureCode: feature?.code,
			doEnable,
		}));

		setFeaturesToConfirm([
			...featuresToConfirm.filter(
				(f) => !newFeatures.some((nf) => nf.featureCode === f.featureCode)
			),
			...newFeatures.map((nf) => ({
				featureCode: nf.featureCode ?? null,
				doEnable: nf.doEnable,
			})),
		]);

		setSelectionModel([]);
	};

	const handleRemoveFeature = (featureCode: string) => {
		setFeaturesToConfirm(
			featuresToConfirm.filter((feature) => feature.featureCode !== featureCode)
		);
	};

	const handleConfirmChanges = () => {
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}

		const featureEnableDisableMessage: FeatureEnableDisableMessage = {
			requestId: uuidv4(),
			reason: reasonText,
			filteredDeviceNames: deviceForm.devices
				.filter((x) => x.isChecked)
				.map((x) => x.name),
			features: featuresToConfirm,
		};

		onEnableDisable(featureEnableDisableMessage);
		setFeaturesToConfirm([]);
	};

	const handleClearChanges = () => {
		setFeaturesToConfirm([]);
	};

	const handleInvertAll = () => {
		setFeaturesToConfirm(
			featuresToConfirm.map((feature) => ({
				...feature,
				doEnable: typeof feature.doEnable === "boolean" ? !feature.doEnable : true,
			}))
		);
	};

	const handleReasonTextFieldChange = (
		event: ChangeEvent<HTMLInputElement>
	) => {
		setReasonText(event.target.value);
	};

	const [reasonText, setReasonText] = useState("");
	const debouncedReasonText = useDebounce(reasonText, 300);

	useEffect(() => {
		setReasonText(debouncedReasonText);
	}, [debouncedReasonText]);

	const [isDeviceFirstView, setIsDeviceFirstView] = useState(true);

	// biome-ignore lint/correctness/useExhaustiveDependencies: callback factory pattern intentionally captures outer scope values
	const getConfirmDialogContent = useMemo(() => {
		return () => (
			<Stack mt={1} spacing={1}>
				<TextField
					name="reason"
					label="Důvod změny"
					variant="outlined"
					size="small"
					value={reasonText}
					inputProps={{ maxLength: 200 }}
					onChange={handleReasonTextFieldChange}
					sx={{ flex: 1 }}
				/>
				<Stack direction="row" justifyContent="end">
					<Button
						endIcon={<AccountTreeIcon />}
						variant="outlined"
						onClick={() => setIsDeviceFirstView(!isDeviceFirstView)}
						size="small"
					>
						{isDeviceFirstView ? "Dle instance" : "Dle features"}
					</Button>
				</Stack>
				{isDeviceFirstView ? renderDeviceFirstView() : renderFeatureFirstView()}
			</Stack>
		);
	}, [
		isDeviceFirstView,
		featuresToConfirm,
		featureDeviceList,
		reasonText,
		deviceForm.devices,
	]);

	const renderFeatureFirstView = useMemo(() => {
		return () => (
			<Stack spacing={2}>
				<TableContainer component={Paper}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>
									<strong>FeatureCode</strong>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{featuresToConfirm.length === 0 ? (
								<TableRow>
									<TableCell colSpan={4} align="center">
										<Typography variant="subtitle2">Žádné změny</Typography>
									</TableCell>
								</TableRow>
							) : (
								featuresToConfirm.map((featureToConfirm) => {
									const feature = featureDeviceList.find(
										(f) => f.feature.code === featureToConfirm.featureCode
									);

									return (
										<React.Fragment key={featureToConfirm.featureCode}>
											<TableRow>
												<TableCell>{feature?.feature.code}</TableCell>
											</TableRow>

											{feature?.devices.map(
												(device) =>
													deviceForm.devices.find(
														(d) => d.name === device.deviceName
													)?.isChecked && (
														<TableRow key={device.deviceName}>
															<TableCell sx={{ pl: 4 }}>
																<Stack
																	direction="row"
																	justifyContent="space-between"
																	alignItems="center"
																>
																	<span>{device.deviceName}</span>
																	{featureToConfirm.doEnable !== undefined && (
																		<Stack
																			direction="row"
																			justifyContent="space-between"
																			alignItems="center"
																			spacing={1}
																		>
																			{device.isEnabled ? (
																				<CheckCircleIcon
																					color="success"
																					fontSize="small"
																				/>
																			) : (
																				<CancelIcon
																					color="error"
																					fontSize="small"
																				/>
																			)}
																			<span>→</span>
																			{featureToConfirm.doEnable ? (
																				<CheckCircleIcon
																					color="success"
																					fontSize="small"
																				/>
																			) : (
																				<CancelIcon
																					color="error"
																					fontSize="small"
																				/>
																			)}
																		</Stack>
																	)}
																</Stack>
															</TableCell>
														</TableRow>
													)
											)}
										</React.Fragment>
									);
								})
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Stack>
		);
	}, [featuresToConfirm, featureDeviceList, deviceForm.devices]);

	const renderDeviceFirstView = () => (
		<Stack spacing={2}>
			{deviceForm.devices.map((device) => {
				if (!device.isChecked) return null;

				const associatedFeatures = featuresToConfirm.filter(
					(featureToConfirm) =>
						featureDeviceList.some(
							(feature) =>
								feature.feature.code === featureToConfirm.featureCode &&
								feature.devices.some((d) => d.deviceName === device.name)
						)
				);

				return (
					<Box key={device.name} sx={{ mb: 3 }}>
						<TableContainer component={Paper}>
							<Typography
								fontSize="1.2rem"
								fontWeight="bold"
								sx={{ m: 0.5, ml: 1 }}
							>
								{device.name}
							</Typography>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>
											<strong>FeatureCode</strong>
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{associatedFeatures.length === 0 ? (
										<TableRow>
											<TableCell colSpan={3} align="center">
												<Typography variant="subtitle2">Žádné změny</Typography>
											</TableCell>
										</TableRow>
									) : (
										associatedFeatures.map((featureToConfirm) => {
											const feature = featureDeviceList.find(
												(f) => f.feature.code === featureToConfirm.featureCode
											);
											const deviceData = feature?.devices.find(
												(d) => d.deviceName === device.name
											);

											return (
												<TableRow key={featureToConfirm.featureCode}>
													<TableCell>
														<Stack
															direction="row"
															justifyContent="space-between"
															alignItems="center"
														>
															<span>{feature?.feature.code}</span>
															{featureToConfirm.doEnable !== undefined && (
																<Stack
																	direction="row"
																	justifyContent="space-between"
																	alignItems="center"
																	spacing={1}
																>
																	{deviceData?.isEnabled ? (
																		<CheckCircleIcon
																			color="success"
																			fontSize="small"
																		/>
																	) : (
																		<CancelIcon
																			color="error"
																			fontSize="small"
																		/>
																	)}
																	<span>→</span>
																	{featureToConfirm.doEnable ? (
																		<CheckCircleIcon
																			color="success"
																			fontSize="small"
																		/>
																	) : (
																		<CancelIcon
																			color="error"
																			fontSize="small"
																		/>
																	)}
																</Stack>
															)}
														</Stack>
													</TableCell>
												</TableRow>
											);
										})
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				);
			})}
		</Stack>
	);

	const getMessageSummary = () => {
		const items: string[] = [];
		if (!!featuresToConfirm && featuresToConfirm.length > 0) {
			const enabledCount = featuresToConfirm.filter(f => f.doEnable === true).length;
			const disabledCount = featuresToConfirm.filter(f => f.doEnable === false).length;
			
			if (enabledCount > 0) {
				items.push(`zapnout: ${enabledCount}`);
			}
			if (disabledCount > 0) {
				items.push(`vypnout: ${disabledCount}`);
			}
		}

		return items;
	};

	return (
		<Box>
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				sx={{ mb: 1 }}
			>
				<Stack direction="row" alignItems="center" gap={1}>
					<Button
						onClick={() => handleEnableDisable(true)}
						disabled={!selectionModel.length}
					>
						Zapnout
					</Button>
					<Button
						onClick={() => handleEnableDisable(false)}
						disabled={!selectionModel.length}
					>
						Vypnout
					</Button>
					<Box borderLeft="1px solid gray" sx={{ height: 20 }} />
					{/* <BlConfirmButton
						modalTitle={"Zapnutí / Vypnutí"}
						modalContent={getConfirmDialogContent()}
						modalMaxWidth="1024px"
						onResult={(result: any) => {
							if (!!result) {
								handleConfirmChanges();
							}
						}}
						disabled={!featuresToConfirm.length}
					>
						Potvrdit
					</BlConfirmButton>
					<Button
						onClick={handleClearChanges}
						disabled={!featuresToConfirm.length}
						color="error"
					>
						Zrušit vše
					</Button> */}
					<Stack direction="row" alignItems="center" spacing={0.1}>
						<BlConfirmButton
							iconButton
							icon={<SaveIcon />}
							modalTitle={"Zpracovat vybrané změny"}
							modalContent={getConfirmDialogContent()}
							modalMaxWidth="1024px"
							onResult={(result: any) => {
								if (!!result) {
									handleConfirmChanges();
								}
							}}
							disabled={!featuresToConfirm.length}
							variant="primary"
							tooltip={"Zpracovat vybrané změny"}
							tooltipPlacement="top"
						>
							Potvrdit
						</BlConfirmButton>
						<BlConfirmButton
							iconButton
							icon={<SwapHorizIcon />}
							modalTitle={"Invertovat vybrané změny?"}
							modalContent={""}
							onResult={(result: any) => {
								if (!!result) {
									handleInvertAll();
								}
							}}
							disabled={!featuresToConfirm.length || !isHistoryLoaded}
							variant="primary"
							tooltip={"Invertovat vybrané změny u historické akce"}
							tooltipPlacement="top"
						>
							Invertovat
						</BlConfirmButton>

						<BlTooltip title="Zrušit vybrané změny" placement="top">
							<IconButton
								onClick={handleClearChanges}
								disabled={!featuresToConfirm.length}
								color="primary"
							>
								<ClearAllIcon />
							</IconButton>
						</BlTooltip>
					</Stack>
				</Stack>
				<Stack direction="row" alignItems="center" gap={1}>
					<Typography sx={{ color: "text.secondary", flex: 1 }}>
						{getMessageSummary().length > 0 ? (
							<>
								<strong>Vybrané změny</strong> –{" "}
							</>
						) : (
							"Žádný výběr"
						)}
						{getMessageSummary().join(", ")}
					</Typography>
				</Stack>
				<Stack direction="row" alignItems="center" gap={1}>
					<Select
						value={selectedFeatureType}
						onChange={(event) => setSelectedFeatureType(event.target.value)}
						displayEmpty
						size="small"
						sx={{ width: 150 }}
					>
						<MenuItem
							value=""
							sx={{
								paddingY: 0.2,
							}}
						>
							<em>Nevybráno</em>
						</MenuItem>
						{Array.from(new Set(rows.map((row) => row.featureType))).map(
							(type) => (
								<MenuItem
									key={type}
									value={type}
									sx={{
										paddingY: 0.2,
									}}
								>
									{GetNsureFeatureTypeDisplayName(type as number)}
								</MenuItem>
							)
						)}
					</Select>
					<Select
						value={selectedFeatureCategory}
						onChange={(event) => setSelectedFeatureCategory(event.target.value)}
						displayEmpty
						size="small"
						variant="outlined"
						sx={{ width: 150 }}
					>
						<MenuItem
							value=""
							sx={{
								paddingY: 0.2,
							}}
						>
							<em>Nevybráno</em>
						</MenuItem>
						{Array.from(new Set(rows.map((row) => row.featureCategory))).map(
							(category) => (
								<MenuItem
									key={category}
									value={category}
									sx={{
										paddingY: 0.2,
									}}
								>
									{GetNsureFeatureCategoryDisplayName(category as number)}
								</MenuItem>
							)
						)}
					</Select>
					<TextField
						variant="outlined"
						placeholder="Hledat"
						value={filterText}
						onChange={(e) => setFilterText(e.target.value)}
						size="small"
						sx={{ width: 200 }}
					/>
				</Stack>
			</Stack>
			<div style={{ height: 500, width: "100%" }}>
				<DataGrid
					localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
					rows={filteredRows}
					columns={columns}
					checkboxSelection
					onSelectionModelChange={(newSelection) =>
						setSelectionModel(newSelection)
					}
					selectionModel={selectionModel}
					pagination
					paginationMode="client"
					pageSize={20}
					rowsPerPageOptions={[20]}
					density="compact"
					// autoHeight
					disableColumnMenu
					getRowClassName={(params) => {
						const featureToConfirm = featuresToConfirm.find(
							(f) => f.featureCode === params.row.code
						);
						if (!!featureToConfirm && featureToConfirm.doEnable !== undefined) {
							return featureToConfirm.doEnable ? "enable-row" : "disable-row";
						}
						return "";
					}}
					sx={{
						height: "814px",
						"& .enable-row": {
							backgroundColor: "rgb(250, 255, 248)",
						},
						"& .disable-row": {
							backgroundColor: "rgb(255, 248, 248)",
						},
						"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
							outline: "none !important",
						},
					}}
				/>
			</div>
		</Box>
	);
};

export default FeatureTable;
